import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Mail, X, Info, Check, Trash2 } from "lucide-react";
import FinanceLayout from "@/components/FinanceLayout";
import MonthPicker from "@/components/MonthPicker";
import { useWorkspace } from "@/context/WorkspaceContext";
import { api, formatApiError } from "@/lib/api";
import { fmtTL, BILLING_STATUSES, BILLING_METHODS, billingStatusMeta, MONTHS_TR, onetimeStatusMeta, fmtDateTR } from "@/lib/finance";
import { toast } from "sonner";

const pad = (n) => String(n).padStart(2, "0");

function InlineSelect({ value, options, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null); // {top,left,width,openUp}
  const btnRef = useRef(null);
  const sel = options.find((o) => o.value === value);

  const openMenu = () => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const menuH = Math.min(options.length * 36 + 8, 320);
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < menuH + 8;
    setPos({
      top: openUp ? r.top - 6 : r.bottom + 4,
      left: r.left,
      minWidth: Math.max(r.width, 180),
      openUp,
    });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const reposition = () => setOpen(false);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  return (
    <div className="relative inline-block" data-testid={testId}>
      <button ref={btnRef} type="button" onClick={() => (open ? setOpen(false) : openMenu())} className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50">
        {sel?.dot && <span className={`w-2 h-2 rounded-full ${sel.dot}`} />}<span>{sel?.label || "—"}</span>
      </button>
      {open && pos && createPortal(
        <>
          <div className="fixed inset-0 z-[100]" onClick={() => setOpen(false)} />
          <div
            className="fixed z-[110] rounded-md border border-slate-200 bg-white shadow-lg py-1 max-h-[320px] overflow-y-auto"
            style={{
              top: pos.openUp ? undefined : pos.top,
              bottom: pos.openUp ? window.innerHeight - pos.top : undefined,
              left: pos.left,
              minWidth: pos.minWidth,
            }}
          >
            {options.map((o) => (
              <button key={o.value} type="button" onClick={() => { onChange(o.value); setOpen(false); }} data-testid={`${testId}-opt-${o.value}`}
                className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                {o.dot && <span className={`w-2 h-2 rounded-full ${o.dot}`} />}{o.label}
              </button>
            ))}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

const Box = ({ label, value, testId, tone = "emerald", detail }) => {
  const toneCls = { emerald: "border-emerald-100 bg-emerald-50/40", red: "border-red-100 bg-red-50/40", sky: "border-sky-100 bg-sky-50/40", slate: "border-slate-100 bg-slate-50/40", amber: "border-amber-100 bg-amber-50/40" }[tone] || "border-slate-100 bg-white";
  return (
    <div className={`rounded-xl border ${toneCls} p-4 relative group`} data-testid={testId}>
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      <div className="text-lg font-bold text-slate-900 tracking-tight">{value}</div>
      {detail && Object.keys(detail).length > 0 && (
        <div className="absolute top-2 right-2"><Info size={12} className="text-slate-300" />
          <div className="opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none absolute right-0 top-5 z-30 min-w-[220px] rounded-lg border border-slate-200 bg-white shadow-lg p-3 text-xs text-slate-700">
            {Object.entries(detail).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3 py-0.5"><span className="text-slate-500">{k}</span><span className="font-semibold">{fmtTL(v)}</span></div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function MonthlyBilling() {
  const { workspace } = useWorkspace();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState({ items: [] });
  const [summary, setSummary] = useState(null);
  const [payDlg, setPayDlg] = useState(null); // {row, status, amount}
  const [reminderDlg, setReminderDlg] = useState(false);
  const [reminderPreview, setReminderPreview] = useState(null);
  const [sending, setSending] = useState(false);

  const load = async () => {
    const [{ data: mb }, { data: s }] = await Promise.all([
      api.get("/finance/monthly-billing", { params: { workspace, year, month } }),
      api.get("/finance/monthly-summary", { params: { workspace, year, month } }),
    ]);
    setData(mb); setSummary(s);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspace, year, month]);

  const patch = async (row, changes) => {
    try {
      const { data: fresh } = await api.put(`/finance/monthly-billing/${row.id}`, changes);
      setData((d) => ({ ...d, items: d.items.map((x) => x.id === row.id ? fresh : x) }));
      load();
    } catch (err) { toast.error("Güncellenemedi"); }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`${row.brand_name} — ${MONTHS_TR[month - 1]} ${year} bilanço satırı silinsin mi? Ciro rakamları buna göre güncellenir.`)) return;
    try {
      await api.delete(`/finance/monthly-billing/${row.id}`);
      toast.success("Satır silindi");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail, "Silinemedi")); }
  };

  const deleteOnetime = async (j) => {
    if (!window.confirm(`${j.brand_name} tek seferlik işi silinsin mi? Bu ayın cirosuna etkisi de kaldırılır.`)) return;
    try {
      await api.delete(`/finance/onetime-jobs/${j.id}`);
      toast.success("Tek seferlik iş silindi");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail, "Silinemedi")); }
  };

  const handleStatusChange = (row, newStatus) => {
    if (newStatus === "odedi" || newStatus === "kismi_odeme") {
      const remaining = (row.amount || 0) - (row.partial_amount || 0);
      setPayDlg({ row, status: newStatus, amount: newStatus === "odedi" ? remaining : "" });
    } else {
      patch(row, { status: newStatus, partial_amount: 0 });
    }
  };

  const confirmPay = async () => {
    const { row, status, amount } = payDlg;
    const numeric = parseFloat(amount || 0);
    if (status === "kismi_odeme" && (!numeric || numeric <= 0)) return toast.error("Kısmi ödeme tutarı giriniz");
    const cur = (row.partial_amount || 0);
    const changes = status === "odedi"
      ? { status: "odedi", partial_amount: row.amount || 0 }
      : { status: "kismi_odeme", partial_amount: cur + numeric };
    await patch(row, changes);
    setPayDlg(null);
  };

  const openReminder = () => { setReminderDlg(true); setReminderPreview(null); };
  const sendReminder = async (row) => {
    setSending(true);
    try {
      const { data: res } = await api.post(`/finance/monthly-billing/${row.id}/reminder`);
      toast.success(`E-posta gönderildi: ${res.sent_to}`);
      setReminderPreview({ row, message: res.message });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSending(false); }
  };

  const items = data.items || [];

  return (
    <FinanceLayout>
      <div className="mb-8 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">Aylık Toplam Bilanço</h1>
          <p className="text-sm text-slate-500 mt-2">Aylık anlaşmalarınızın ödeme durumlarını ve detaylarını görüntüleyin.</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={openReminder} data-testid="mb-reminder-btn" className="rounded-lg bg-emerald-800 hover:bg-emerald-700 text-white px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
            <Mail size={14} /> Hatırlatma Maili Gönder
          </button>
          <MonthPicker year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} testId="mb-monthpicker" />
        </div>
      </div>

      <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden mb-6">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/40">
              {["Gün", "Marka Adı", "Aylık Anlaşma", "Sabit Reklam Gideri", "Durum", "Ödeme Yöntemi", "Not", ""].map((h, i) => (
                <th key={i} className="text-left py-4 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={8} className="py-14 text-center text-sm text-slate-500" data-testid="mb-empty">Bu ay için kayıt yok.</td></tr>
            ) : items.map((r) => {
              const meta = billingStatusMeta(r.status);
              const remaining = Math.max(0, (r.amount || 0) - (r.partial_amount || 0));
              return (
                <tr key={r.id} className="border-b border-slate-50 last:border-none hover:bg-slate-50/40" data-testid={`mb-row-${r.id}`}>
                  <td className="py-4 px-4 text-sm text-slate-700 whitespace-nowrap">{pad(r.payment_day)} {MONTHS_TR[month - 1]} {year}</td>
                  <td className="py-4 px-4 text-sm font-medium text-slate-900">
                    <Link to={`/finance/firm-billing?brand=${encodeURIComponent(r.brand_name)}`} className="hover:text-emerald-700">{r.brand_name}</Link>
                  </td>
                  <td className="py-4 px-4">
                    <div className="text-sm font-semibold text-slate-900">{fmtTL(r.amount)}</div>
                    {r.partial_amount > 0 && r.status !== "odedi" && (
                      <div className="text-xs text-sky-700 mt-0.5" data-testid={`mb-partial-${r.id}`}>Ödenen: {fmtTL(r.partial_amount)} · Kalan: {fmtTL(remaining)}</div>
                    )}
                  </td>
                  <td className="py-4 px-4">
                    <input type="number" step="0.01" defaultValue={r.fixed_expense ?? 0}
                      onBlur={(e) => { const v = parseFloat(e.target.value || 0); if (v !== (r.fixed_expense ?? 0)) patch(r, { fixed_expense_override: v }); }}
                      data-testid={`mb-fixed-${r.id}`}
                      className="w-28 h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-right focus:outline-none focus:ring-1 focus:ring-emerald-500" />
                  </td>
                  <td className="py-4 px-4">
                    <span className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-md text-xs font-medium border ${meta.chip}`}>
                      <span className={`w-2 h-2 rounded-full ${meta.dot}`} />{meta.label}
                    </span>
                    <div className="mt-1"><InlineSelect value={r.status} options={BILLING_STATUSES} onChange={(v) => handleStatusChange(r, v)} testId={`mb-status-${r.id}`} /></div>
                  </td>
                  <td className="py-4 px-4"><InlineSelect value={r.payment_method || ""} options={BILLING_METHODS} onChange={(v) => patch(r, { payment_method: v })} testId={`mb-method-${r.id}`} /></td>
                  <td className="py-4 px-4">
                    <input type="text" defaultValue={r.note || ""} onBlur={(e) => e.target.value !== (r.note || "") && patch(r, { note: e.target.value })} placeholder="Not…" data-testid={`mb-note-${r.id}`}
                      className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500" />
                  </td>
                  <td className="py-4 px-4">
                    <button onClick={() => deleteRow(r)} data-testid={`mb-del-${r.id}`} title="Satırı Sil"
                      className="w-8 h-8 rounded-md hover:bg-red-50 text-red-500 hover:text-red-600 inline-flex items-center justify-center">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Tek seferlik işler */}
      <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-slate-100 font-semibold text-slate-900 flex items-center justify-between">
          <span>Tek Seferlik İşler — {MONTHS_TR[month - 1]} {year}</span>
          <span className="text-xs text-slate-500" data-testid="mb-ot-count">{summary?.tek_seferlik_count || 0} kayıt</span>
        </div>
        {(!summary?.onetime_jobs || summary.onetime_jobs.length === 0) ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="mb-ot-empty">Bu ay tek seferlik iş yok.</div>
        ) : (
          <table className="w-full">
            <thead><tr className="border-b border-slate-100 bg-slate-50/40">
              {["Tarih", "Marka", "Anlaşma Bedeli", "Alınan", "Durum", ""].map((h, i) => <th key={i} className="text-left py-3 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>)}
            </tr></thead>
            <tbody>
              {summary.onetime_jobs.map((j) => {
                const paid = Number(j.partial_payment || 0);
                const isDone = j.status === "tamamlandi";
                const revenue = isDone ? Number(j.amount || 0) : paid;
                return (
                  <tr key={j.id} className="border-b border-slate-50 last:border-none" data-testid={`mb-ot-row-${j.id}`}>
                    <td className="py-3 px-6 text-sm text-slate-700">{fmtDateTR(j.date)}</td>
                    <td className="py-3 px-6 text-sm font-medium text-slate-900">{j.brand_name}</td>
                    <td className="py-3 px-6 text-sm text-slate-700">{fmtTL(j.amount)}</td>
                    <td className="py-3 px-6 text-sm font-semibold text-emerald-700">{fmtTL(revenue)}</td>
                    <td className="py-3 px-6 text-sm text-slate-700">{onetimeStatusMeta(j.status)?.label || j.status}</td>
                    <td className="py-3 px-6">
                      <button onClick={() => deleteOnetime(j)} data-testid={`mb-ot-del-${j.id}`} title="Kaydı Sil"
                        className="w-8 h-8 rounded-md hover:bg-red-50 text-red-500 hover:text-red-600 inline-flex items-center justify-center">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Özet kutuları */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="mb-summary">
          <Box label="Sürekli Müşteri Cirosu" value={fmtTL(summary.surekli_ciro)} tone="emerald" testId="sum-surekli-ciro" />
          <Box label="Tek Seferlik İş Cirosu" value={fmtTL(summary.tek_seferlik_ciro)} tone="emerald" testId="sum-ot-ciro" />
          <Box label="İş Bazlı Sabit Gider" value={fmtTL(summary.is_bazli_sabit_gider)} tone="red" detail={summary.is_bazli_breakdown} testId="sum-is-bazli" />
          <Box label="Sabit İşletme Giderleri" value={fmtTL(summary.sabit_isletme_gideri)} tone="red" detail={summary.sabit_isletme_breakdown} testId="sum-isletme" />
          <Box label="Personel Giderleri" value={fmtTL(summary.personel_gideri)} tone="red" detail={summary.personel_breakdown} testId="sum-personel" />
          <Box label="Diğer Giderler" value={fmtTL(summary.diger_gider)} tone="red" detail={summary.diger_breakdown} testId="sum-diger" />
          <Box label="Tahsil Edilen Tutar" value={fmtTL(summary.tahsil_edilen)} tone="sky" testId="sum-tahsil" />
        </div>
      )}

      {/* Payment amount dialog */}
      {payDlg && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center px-6" data-testid="pay-dialog">
          <div className="bg-white rounded-xl max-w-[420px] w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{payDlg.status === "odedi" ? "Ödeme Tutarı" : "Kısmi Ödeme Tutarı"}</h3>
              <button onClick={() => setPayDlg(null)} data-testid="pay-close"><X size={18} /></button>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              <span className="font-medium text-slate-800">{payDlg.row.brand_name}</span> — Anlaşma: {fmtTL(payDlg.row.amount)}
              {payDlg.row.partial_amount > 0 && <> · Daha önce alınan: {fmtTL(payDlg.row.partial_amount)}</>}
            </p>
            <label className="block text-sm font-medium text-slate-700 mb-2">Alınan Tutar (₺)</label>
            <input type="number" step="0.01" autoFocus value={payDlg.amount} onChange={(e) => setPayDlg({ ...payDlg, amount: e.target.value })}
              data-testid="pay-amount"
              className="w-full h-11 rounded-lg border border-slate-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500 mb-4" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setPayDlg(null)} className="rounded-full border border-slate-200 px-4 py-2 text-sm">İptal</button>
              <button onClick={confirmPay} data-testid="pay-confirm" className="rounded-full bg-emerald-800 hover:bg-emerald-700 text-white px-5 py-2 text-sm font-semibold inline-flex items-center gap-1.5"><Check size={13} /> Onayla</button>
            </div>
          </div>
        </div>
      )}

      {/* Reminder dialog */}
      {reminderDlg && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center px-6" data-testid="reminder-dialog">
          <div className="bg-white rounded-xl max-w-[560px] w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Hatırlatma Maili Gönder</h3>
              <button onClick={() => { setReminderDlg(false); setReminderPreview(null); }} data-testid="reminder-close"><X size={18} /></button>
            </div>
            {reminderPreview ? (
              <div>
                <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-4 py-3 mb-3" data-testid="reminder-sent">✓ Gönderildi: {reminderPreview.row.brand_name}</div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 whitespace-pre-wrap" data-testid="reminder-preview">{reminderPreview.message}</div>
                <div className="flex justify-end mt-4">
                  <button onClick={() => { setReminderDlg(false); setReminderPreview(null); }} className="rounded-full bg-slate-900 text-white px-5 py-2 text-sm">Kapat</button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm text-slate-500 mb-3">Bu ayki firmalardan birini seçin.</p>
                <div className="max-h-[320px] overflow-y-auto space-y-1">
                  {items.length === 0 ? (
                    <div className="text-sm text-slate-500 text-center py-8">Bu ay için firma yok.</div>
                  ) : items.map((r) => (
                    <button key={r.id} onClick={() => sendReminder(r)} disabled={sending} data-testid={`reminder-firm-${r.id}`}
                      className="w-full text-left flex items-center justify-between px-4 py-3 rounded-lg border border-slate-100 bg-white hover:bg-emerald-50 hover:border-emerald-200 transition-colors">
                      <div>
                        <div className="text-sm font-semibold">{r.brand_name}</div>
                        <div className="text-xs text-slate-500">Gün {r.payment_day} · {fmtTL(r.amount)}</div>
                      </div>
                      <Mail size={14} className="text-slate-400" />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </FinanceLayout>
  );
}
